import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDecryptedMedia } from '@/hooks/useDecryptedMedia';

interface VoiceNotePlayerProps {
  url: string;
  duration?: number;
  isOwn?: boolean;
  /** Per-file decryption key for Signal-grade encrypted media */
  fileKey?: string | null;
}

export function VoiceNotePlayer({ url, duration, isOwn, fileKey }: VoiceNotePlayerProps) {
  const { decryptedUrl, isDecrypting, error: decryptError } = useDecryptedMedia(url, fileKey, 'audio/webm');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration || 0);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  useEffect(() => {
    if (!decryptedUrl) return;
    
    const audio = new Audio();
    // Set attributes before src for mobile compatibility
    audio.preload = 'auto';
    (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
    audio.src = decryptedUrl;
    audioRef.current = audio;

    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setTotalDuration(audio.duration);
      }
    };
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration && isFinite(audio.duration)) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };
    const onError = () => {
      console.error('[VoiceNote] Audio error:', audio.error?.message || 'unknown');
      setIsPlaying(false);
      setPlaybackError('Playback failed');
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [decryptedUrl]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    setPlaybackError(null);

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (err: unknown) {
        console.error('[VoiceNote] Play failed:', err);
        // Try muted fallback (autoplay policy workaround)
        try {
          audio.muted = true;
          await audio.play();
          audio.muted = false;
          setIsPlaying(true);
        } catch {
          setPlaybackError('Tap again to play');
          setIsPlaying(false);
        }
      }
    }
  }, [isPlaying]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Organic waveform
  const bars = Array.from({ length: 28 }, (_, i) => {
    const height = 6 + Math.sin(i * 0.8) * 10 + Math.cos(i * 1.5) * 6;
    return Math.max(3, Math.min(24, height));
  });

  if (isDecrypting) {
    return (
      <div className="flex items-center gap-2.5 min-w-[200px]">
        <Loader2 className={`h-5 w-5 animate-spin ${isOwn ? 'text-primary-foreground/50' : 'text-muted-foreground/50'}`} />
        <span className={`text-[11px] ${isOwn ? 'text-primary-foreground/50' : 'text-muted-foreground/60'}`}>Decrypting...</span>
      </div>
    );
  }

  if (decryptError || playbackError) {
    return (
      <div className="flex items-center gap-2 min-w-[200px]">
        {playbackError ? (
          // Recoverable - let them tap again
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={togglePlay}
            className={`flex items-center gap-2 text-xs ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}
          >
            <Play className="h-4 w-4 shrink-0" />
            <span>{playbackError}</span>
          </motion.button>
        ) : (
          <>
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            <span className="text-xs text-destructive">Failed to decrypt</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2.5 min-w-[200px]">
      {/* Play/Pause - pill button */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={togglePlay}
        className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
          isOwn
            ? 'bg-primary-foreground/20 hover:bg-primary-foreground/30'
            : 'bg-foreground/10 hover:bg-foreground/15'
        }`}
      >
        {isPlaying ? (
          <Pause className={`h-3.5 w-3.5 ${isOwn ? 'text-primary-foreground' : 'text-foreground'}`} />
        ) : (
          <Play className={`h-3.5 w-3.5 ml-0.5 ${isOwn ? 'text-primary-foreground' : 'text-foreground'}`} />
        )}
      </motion.button>

      {/* Waveform */}
      <div className="flex-1 flex items-center gap-[2px] h-6">
        {bars.map((h, i) => {
          const barProgress = (i / bars.length) * 100;
          const isActive = barProgress <= progress;
          return (
            <div
              key={i}
              className={`w-[3px] rounded-full transition-all duration-150 ${
                isActive
                  ? isOwn ? 'bg-primary-foreground' : 'bg-primary'
                  : isOwn ? 'bg-primary-foreground/25' : 'bg-muted-foreground/25'
              }`}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>

      {/* Duration */}
      <span className={`text-[11px] tabular-nums shrink-0 font-medium ${
        isOwn ? 'text-primary-foreground/50' : 'text-muted-foreground/60'
      }`}>
        {isPlaying ? formatTime(currentTime) : formatTime(totalDuration)}
      </span>
    </div>
  );
}
